import { ComboboxData } from 'ui';

import { isGroup } from './isGroup';

/** Given a ComboboxData, it will filter the data based on the search value and return the original filtered data structure
 *
 * @example
 * const data = [
 *   { value: 'apple', label: 'Apple' },
 *   { value: 'banana', label: 'Banana' },
 *   { value: 'cherry', label: 'Cherry' },
 * ];
 * const filteredData = filterSearchableData(data, 'a');
 * console.log(filteredData);
 * // Output: [{ value: 'apple', label: 'Apple' }, { value: 'banana', label: 'Banana' }]
 *
 * @example
 * const data = [
 *  {
 *    group: 'Fruits',
 *    items: [
 *      { value: 'apple', label: 'Apple' },
 *      { value: 'banana', label: 'Banana' },
 *    ],
 *  },
 *  {
 *    group: 'Vegetables',
 *    items: [
 *      { value: 'carrot', label: 'Carrot' },
 *      { value: 'broccoli', label: 'Broccoli' },
 *    ],
 *  },
 * ];
 * const filteredData = filterSearchableData(data, 'a');
 * console.log(filteredData);
 * Output: [
 * { group: 'Fruits', items: [{ value: 'apple', label: 'Apple' }, { value: 'banana', label: 'Banana' }] },
 * { group: 'Vegetables', items: [{ value: 'carrot', label: 'Carrot' }] }
 * ]
 */
const filterSearchableData = <T extends ComboboxData>(data: T, searchValue: string): T => {
  if (!searchValue) return data;

  const filteredData: T[number][] = [];

  data.forEach((item) => {
    if (isGroup(item)) {
      const items = item.items;
      const filteredItems = filterSearchableData(items, searchValue);
      filteredData.push({
        ...item,
        items: filteredItems,
      } as T[number]);
    }

    if (typeof item === 'string') {
      if (item.toLowerCase().includes(searchValue.toLowerCase())) {
        filteredData.push(item as T[number]);
      }
    }

    if (typeof item === 'object' && 'value' in item) {
      if (
        item.value.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.label.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        filteredData.push(item as T[number]);
      }
    }
  });

  return filteredData as T;
};

export { filterSearchableData };
